import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import { brandsAPI } from '../services/api';

const emptyForm = {
  name: '',
  name_ar: '',
  sort_order: '0',
  is_active: true,
};

const BrandFormPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(brandId);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) {
      setFormData(emptyForm);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await brandsAPI.get(brandId);
        const brand = response.data?.brand;
        if (!cancelled) {
          if (brand) {
            setFormData({
              name: brand.name || '',
              name_ar: brand.name_ar || '',
              sort_order: String(brand.sort_order ?? 0),
              is_active: Boolean(brand.is_active),
            });
          } else {
            toast.error('Brand not found');
          }
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Brand name is required');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      name_ar: formData.name_ar?.trim() || null,
      sort_order: parseInt(formData.sort_order, 10) || 0,
      is_active: formData.is_active,
    };

    try {
      if (isEdit) {
        await brandsAPI.update(brandId, payload);
        toast.success('Brand updated');
      } else {
        await brandsAPI.create(payload);
        toast.success('Brand created');
      }
      navigate('/brands');
    } catch (error) {
      toast.error('Error saving brand: ' + (error.response?.data?.message || error.message));
    }
  };

  const title = isEdit ? 'Edit brand' : 'Add brand';

  return (
    <AdminSubpage title={title} backTo="/brands">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={submit}>
            <div className="form-group">
              <label>Brand Name *</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Brand name (Arabic)</label>
              <input
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />{' '}
                Active
              </label>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" type="submit">
                {isEdit ? 'Update' : 'Create'}
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => navigate('/brands')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default BrandFormPage;
